import React, { useEffect, useCallback, useState } from "react";
import { Layout, Card, Row, Col, Table, Tag, Typography, Statistic, Progress, Avatar, Badge, Button, Popconfirm, message, DatePicker, Space, Tabs, Modal as AntModal } from "antd";
import TopHeader from "../../common/TopHeader/TopHeader";
import { Link } from "react-router-dom";
import Sidebar from "../../common/Sidebar/Sidebar";
import {
  UserOutlined,
  BankOutlined,
  IdcardFilled,
  FormOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  AppstoreOutlined,
  WarningOutlined,
  CalendarOutlined,
  TrophyOutlined,
  EditOutlined,
  HistoryOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import UserApi from "../../common/Apis/UserApi";
import TenantApi from "../../common/Apis/TenantApi";
import CompliantApi from "../../common/Apis/CompliantApi";
import FooterDashboard from "../../common/FooterDashboard/FooterDashboard";
import StorageInstructionsApi from "../../common/Apis/StorageInstructionsApi";
import CleaningTraceabilityApi from "../../common/Apis/CleaningTraceabilityApi";
import moment from "moment";
import AddNoteModal from "../../common/Models/NoteModal/AddNoteModal";
import OperationalDashboard from "./OperationalDashboard";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const AdminPanel = () => {
  const { Content } = Layout;

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));

  let isSUPER_ADMIN = false;
  const isADMIN = decodedToken?.roles.includes("SUPER_ADMIN");
  const [roleCode, setRoleCode] = useState("");

  // Check role based on roleCode (which stores the first role from array)
  const isSystemUser = roleCode === "SYSTEM_USER";
  const isManager = roleCode === "MANAGER";
  const isTenantAdmin = roleCode === "TENANT_ADMIN";
  const isSubTenantAdmin = roleCode === "SUB_TENANT_ADMIN";


  // Check if user should see tasks (SYSTEM_USER or MANAGER)
  const shouldShowTasks = isSystemUser || isManager;

  // State for today's tasks
  const [todayTasks, setTodayTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskStats, setTaskStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
  });

  // State for historical tasks (for both SYSTEM_USER and MANAGER)
  const [historicalTasks, setHistoricalTasks] = useState([]);
  const [historicalTasksLoading, setHistoricalTasksLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState("today");

  // State for Add Note Modal (only for MANAGER)
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  // State for View Note Modal (for SYSTEM_USER)
  const [viewNoteModalVisible, setViewNoteModalVisible] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);

  // Function to convert date string "DD/MM/YYYY" to Unix timestamp
  const convertToUnixTimestamp = (dateString) => {
    const parts = dateString.split("/");
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return Date.UTC(year, month, day);
  };

  // Function to get current date as Unix timestamp
  const getCurrentDateTimestamp = () => {
    const currentDate = moment().format("DD/MM/YYYY");
    return convertToUnixTimestamp(currentDate);
  };

  // Function to format date for API
  const formatDateForAPI = (date) => {
    if (!date) return null;
    return convertToUnixTimestamp(date.format("DD/MM/YYYY"));
  };

  useEffect(() => {
    const userDetailsString = localStorage.getItem("decodedToken");
    if (userDetailsString) {
      const userDetails = JSON.parse(userDetailsString);
      const firstRoleDTO = userDetails.roles && userDetails.roles[0];
      if (firstRoleDTO) {
        setRoleCode(firstRoleDTO);
        console.log("Current user role:", firstRoleDTO);
      }
    }
  }, []);

  if (
    decodedToken?.sub == "admin@admin.com" &&
    decodedToken?.roles.includes("SUPER_ADMIN")
  ) {
    isSUPER_ADMIN = true;
  }
  if (decodedToken) {
    localStorage.setItem("isSUPER_ADMIN", isSUPER_ADMIN);
    localStorage.setItem("isADMIN", isADMIN);
    localStorage.setItem("isUser", isSystemUser);
    localStorage.setItem("UsertenantId", decodedToken?.tenantId);
  }

  const [TenantsUsers, setTenantsUsers] = React.useState([]);
  const [Tenants, setTenants] = React.useState([]);
  const [Complaints, setComplaints] = React.useState([]);
  const [StorageInst, setStorageInst] = React.useState([]);

  const getTenantsUsers = useCallback(() => {
    UserApi.tenantUsers(decodedToken?.tenantId, 0, 10)
      .then((response) => {
        setTenantsUsers(response.payloadDto);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const getTenants = useCallback(() => {
    TenantApi.tenantsList(0, 10)
      .then((response) => {
        setTenants(response.payloadDto);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const getComplaints = useCallback(() => {
    CompliantApi.ListUserCompliants(decodedToken?.sub)
      .then((response) => {
        setComplaints(response.payloadDto);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const getStorageInstruction = useCallback(() => {
    StorageInstructionsApi.ListStorageInstructions(0, 10)
      .then((response) => {
        setStorageInst(response.payloadDto);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  // Function to update task statistics
  const updateTaskStats = (tasks) => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === "completed").length;
    const pending = tasks.filter(t => t.status === "pending").length;
    setTaskStats({ total, completed, pending });
  };

  // Function to mark task as completed (for SYSTEM_USER and MANAGER)
  const markTaskAsCompleted = async (taskId, frequency) => {
    setTasksLoading(true);
    try {
      const formData = JSON.stringify({
        id: taskId,
        frequency: frequency,
        tenantDTO: {
          id: decodedToken?.tenantId,
          tenantApiKey: decodedToken?.tenantApiKey,
        },
      });
      console.log("Marking task as completed with data:", formData);
      const response = await CleaningTraceabilityApi.TasksMarkComplete(taskId, formData);

      if (response) {
        const updatedTasks = todayTasks.map(task =>
          task.id === taskId ? { ...task, status: "completed" } : task
        );
        setTodayTasks(updatedTasks);
        updateTaskStats(updatedTasks);
        message.success("Task marked as completed successfully!");
        await getTodayTasks();
      }
    } catch (err) {
      console.error("Error marking task as completed:", err);
      message.error(err?.apiError?.message || "Failed to mark task as completed");
    } finally {
      setTasksLoading(false);
    }
  };

  // Function to open note modal (for MANAGER)
  const openNoteModal = (task) => {
    setSelectedTask(task);
    setNoteModalVisible(true);
  };

  // Function to view note (for SYSTEM_USER)
  const viewNote = (task) => {
    setViewingTask(task);
    setViewNoteModalVisible(true);
  };

  // Handle note added callback - Update the task with the new note
  const handleNoteAdded = (taskId, newNote) => {
    console.log(`Note added/updated for task ${taskId}:`, newNote);
    
    // Update the task in todayTasks
    setTodayTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId 
          ? { ...task, managerNote: newNote.managerNote }
          : task
      )
    );
    
    // Update the task in historicalTasks if it exists there
    setHistoricalTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === taskId 
          ? { ...task, managerNote: newNote.managerNote }
          : task
      )
    );
    
    // Also update the selectedTask to reflect the change immediately
    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask(prev => ({ ...prev, managerNote: newNote.managerNote }));
    }
  };

  // Function to fetch tasks for a specific date (for both SYSTEM_USER and MANAGER)
  const fetchTasksForDate = async (date) => {
    if (!date) return;
    
    setHistoricalTasksLoading(true);
    try {
      const dateTimestamp = formatDateForAPI(date);
      const frequencies = ["DAILY", "WEEKLY", "MONTHLY"];
      let allTasks = [];

      for (const frequency of frequencies) {
        try {
          const response = await CleaningTraceabilityApi.ListCleaningTraceabilityForToday(
            dateTimestamp,
            frequency
          );

          if (response?.payload && response.payload.length > 0) {
            const mappedTasks = response.payload.map(task => ({
              id: task.id,
              taskName: task.taskMasterDTO?.taskName || "N/A",
              department: task.institutionDTO?.institutionName || "N/A",
              area: task.taskMasterDTO?.areaDTO?.areaName || task.taskMasterDTO?.areaDTO?.name || "N/A",
              frequency: task.taskMasterDTO?.frequency,
              description: task.taskMasterDTO?.description || "-",
              status: task.isCompleted ? "completed" : "pending",
              priority: task.priority || "medium",
              scheduledDate: task.scheduledDate,
              managerNote: task.managerNote || "",
              originalData: task,
            }));
            allTasks = [...allTasks, ...mappedTasks];
          }
        } catch (error) {
          console.error(`Error fetching ${frequency} tasks for date:`, error);
        }
      }

      setHistoricalTasks(allTasks);
      if (allTasks.length === 0) {
        message.info(`No tasks found for ${date.format('DD/MM/YYYY')}`);
      }
    } catch (err) {
      console.error("Error fetching tasks for date:", err);
      message.error("Failed to fetch tasks for selected date");
    } finally {
      setHistoricalTasksLoading(false);
    }
  };

  // Function to fetch today's tasks from API
  const getTodayTasks = useCallback(async () => {
    if (!shouldShowTasks) return;
    
    setTasksLoading(true);
    try {
      const currentDateTimestamp = getCurrentDateTimestamp();
      const frequencies = ["DAILY", "WEEKLY", "MONTHLY"];
      let allTasks = [];

      for (const frequency of frequencies) {
        try {
          const response = await CleaningTraceabilityApi.ListCleaningTraceabilityForToday(
            currentDateTimestamp,
            frequency
          );

          if (response?.payload && response.payload.length > 0) {
            const mappedTasks = response.payload.map(task => ({
              id: task.id,
              taskName: task.taskMasterDTO?.taskName || "N/A",
              department: task.institutionDTO?.institutionName || "N/A",
              area: task.taskMasterDTO?.areaDTO?.areaName || task.taskMasterDTO?.areaDTO?.name || "N/A",
              frequency: task.taskMasterDTO?.frequency,
              description: task.taskMasterDTO?.description || "-",
              status: task.isCompleted ? "completed" : "pending",
              priority: task.priority || "medium",
              scheduledDate: task.scheduledDate,
              managerNote: task.managerNote || "",
              originalData: task,
            }));
            allTasks = [...allTasks, ...mappedTasks];
          }
        } catch (error) {
          console.error(`Error fetching ${frequency} tasks:`, error);
        }
      }

      setTodayTasks(allTasks);
      updateTaskStats(allTasks);
    } catch (err) {
      console.error("Error fetching today's tasks:", err);
      message.error("Failed to fetch today's tasks");
    } finally {
      setTasksLoading(false);
    }
  }, [shouldShowTasks]);

  useEffect(() => {
    getTenantsUsers();
    getTenants();
    getComplaints();
    getStorageInstruction();
    if (shouldShowTasks) {
      getTodayTasks();
    }
  }, [getTenantsUsers, getTenants, getComplaints, getStorageInstruction, getTodayTasks, shouldShowTasks]);

  // Table columns for tasks (reusable for both today and historical)
  const getTaskColumns = (isHistorical = false) => {
    const columns = [
      {
        title: "Task Name",
        dataIndex: "taskName",
        key: "taskName",
        render: (text, record) => (
          <div>
            <Text strong style={{ textDecoration: record.status === "completed" ? "line-through" : "none" }}>
              {text}
            </Text>
            {record.priority === "high" && record.status !== "completed" && (
              <Tag color="red" size="small" style={{ marginLeft: 8 }}>High Priority</Tag>
            )}
          </div>
        ),
      },
      {
        title: "Department",
        dataIndex: "department",
        key: "department",
        render: (text) => <Tag color="geekblue">{text || "-"}</Tag>,
      },
       {
        title: "Description",
        dataIndex: "description",
        key: "description",
        render: (text) => <Tag color="magenta">{text || "-"}</Tag>,
      },
      {
        title: "Area",
        dataIndex: "area",
        key: "area",
        render: (text) => <Text>{text || "-"}</Text>,
      },
      {
        title: "Frequency",
        dataIndex: "frequency",
        key: "frequency",
        render: (text) => (
          <Tag color={text === "DAILY" ? "blue" : text === "WEEKLY" ? "green" : "orange"}>
            {text}
          </Tag>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (text) => {
          if (text === "pending") {
            return (
              <Tag color="orange" icon={<ClockCircleOutlined />}>
                PENDING
              </Tag>
            );
          }
          if (text === "completed") {
            return (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                COMPLETED
              </Tag>
            );
          }
          return <Tag>{text}</Tag>;
        },
      },
    ];

    // Add Manager Note column for SYSTEM_USER
    if (isSystemUser) {
      columns.push({
        title: "Manager Note",
        key: "managerNote",
        render: (_, record) => (
          <div>
            {record.managerNote ? (
              <Button
                type="link"
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => viewNote(record)}
                style={{ padding: 0 }}
              >
                View Note
              </Button>
            ) : (
              <Tag color="default">No Note</Tag>
            )}
          </div>
        ),
      });
    }

    // Add Action column
    columns.push({
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {/* Show Mark Complete button for SYSTEM_USER and MANAGER - only for pending tasks */}
          {(isSystemUser || isManager) && record.status === "pending" && (
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                loading={tasksLoading}
                onClick={() => markTaskAsCompleted(record.id, record.frequency)}
              >
                Mark Complete
              </Button>
          )}
          
          {/* Show Add Note button for MANAGER - regardless of task status */}
          {isManager && (
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openNoteModal(record)}
              style={
                record.managerNote
                  ? { color: '#ff4d4f', borderColor: '#ff4d4f' }
                  : { color: '#1890ff', borderColor: '#1890ff' }
              }
            >
              {record.managerNote ? "Update Note" : "Add Note"}
            </Button>
          )}
        </div>
      ),
    });

    return columns;
  };

  // Stats card configuration
  const getCompletionRate = () => {
    if (taskStats.total === 0) return 0;
    return Math.round((taskStats.completed / taskStats.total) * 100);
  };

  // Tab items for both SYSTEM_USER and MANAGER
  const tabItems = [
    {
      key: "today",
      label: (
        <span>
          <CalendarOutlined /> Today's Tasks
        </span>
      ),
      children: (
        <Table
          columns={getTaskColumns(false)}
          dataSource={todayTasks}
          loading={tasksLoading}
          rowKey="id"
          pagination={{
            pageSize: 8,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} tasks`,
          }}
          locale={{
            emptyText: '🎉 No tasks for today. Great job!',
          }}
        />
      ),
    },
    {
      key: "historical",
      label: (
        <span>
          <HistoryOutlined /> Historical Tasks
        </span>
      ),
      children: (
        <div>
          <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
            <Card size="small">
              <Space wrap>
                <Text strong>Select Date:</Text>
                <DatePicker
                  placeholder="Choose a date"
                  onChange={(date) => {
                    setSelectedDate(date);
                    if (date) {
                      fetchTasksForDate(date);
                    } else {
                      setHistoricalTasks([]);
                    }
                  }}
                  format="DD/MM/YYYY"
                  style={{ width: 200 }}
                  value={selectedDate}
                />
                {selectedDate && (
                  <Button 
                    onClick={() => fetchTasksForDate(selectedDate)}
                    type="primary"
                    loading={historicalTasksLoading}
                  >
                    Load Tasks
                  </Button>
                )}
                {selectedDate && historicalTasks.length > 0 && (
                  <Text type="secondary">
                    Found {historicalTasks.length} task(s)
                  </Text>
                )}
              </Space>
            </Card>
          </Space>
          
          {selectedDate && (
            <Table
              columns={getTaskColumns(true)}
              dataSource={historicalTasks}
              loading={historicalTasksLoading}
              rowKey="id"
              pagination={{
                pageSize: 8,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} tasks`,
              }}
              locale={{
                emptyText: 'No tasks found for this date',
              }}
            />
          )}
          
          {!selectedDate && (
            <Card style={{ textAlign: 'center', padding: '40px' }}>
              <HistoryOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
              <div>
                <Text type="secondary">Select a date above to view historical tasks</Text>
              </div>
            </Card>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Sidebar />
      <Layout style={{ width: "100%" }}>
        <TopHeader />
        <Content style={{ margin: "24px 24px 0" }}>
          {/* Welcome Section */}
          <Card
            style={{
              marginBottom: 24,
              borderRadius: 12,
              background: "linear-gradient(135deg, #667eea 0%, rgb(62, 85, 164) 100%)",
              border: 'none'
            }}
          >
            <div style={{ padding: '0px' }}>
              <Title level={3} style={{ color: 'white', marginBottom: 8 }}>
                Welcome back, {decodedToken?.name || decodedToken?.sub?.split('@')[0] || 'User'}!
              </Title>
              {/* <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 16 }}>
                {isManager 
                  ? "Monitor, manage, and complete cleaning tasks across all dates" 
                  : isSystemUser 
                    ? "View and complete your cleaning tasks. Manager notes are available for reference."
                    : "Here's what's happening with your cleaning operations today."}
              </Text> */}
            </div>
          </Card>

        {(isManager || isSubTenantAdmin) && <OperationalDashboard />}
          {/* Stats Cards Row - Only show for SYSTEM_USER */}
          {isSystemUser && (
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} lg={8}>
                <Card
                  className="stat-card"
                  style={{
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    transition: 'transform 0.3s',
                    cursor: 'pointer'
                  }}
                  hoverable
                >
                  <Statistic
                    title={<span style={{ fontSize: 14, color: '#666' }}>Today's Tasks</span>}
                    value={taskStats.total}
                    prefix={<CalendarOutlined style={{ fontSize: 28, color: '#1890ff' }} />}
                    valueStyle={{ fontSize: 32, fontWeight: 'bold', color: '#1890ff' }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Progress
                      percent={getCompletionRate()}
                      size="small"
                      strokeColor="#52c41a"
                      showInfo={false}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {taskStats.completed} completed out of {taskStats.total}
                    </Text>
                  </div>
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Card
                  style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                  hoverable
                >
                  <Statistic
                    title={<span style={{ fontSize: 14, color: '#666' }}>Completed Tasks</span>}
                    value={taskStats.completed}
                    prefix={<CheckCircleOutlined style={{ fontSize: 28, color: '#52c41a' }} />}
                    valueStyle={{ fontSize: 32, fontWeight: 'bold', color: '#52c41a' }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <TrophyOutlined /> Great progress!
                    </Text>
                  </div>
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={8}>
                <Card
                  style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                  hoverable
                >
                  <Statistic
                    title={<span style={{ fontSize: 14, color: '#666' }}>Pending Tasks</span>}
                    value={taskStats.pending}
                    prefix={<ClockCircleOutlined style={{ fontSize: 28, color: '#ff7c43' }} />}
                    valueStyle={{ fontSize: 32, fontWeight: 'bold', color: '#ff7c43' }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Awaiting completion
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          )}

          {/* Tasks Section - Now with Tabs for both SYSTEM_USER and MANAGER */}
          {shouldShowTasks && (
            <Row gutter={24} style={{ marginTop: isSystemUser ? 24 : 0 }}>
              <Col span={24}>
                <Card
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#1890ff',
                        boxShadow: '0 0 0 2px rgba(24,144,255,0.2)'
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        Task Management
                      </span>
                      {isSystemUser && <Badge count={taskStats.pending} style={{ backgroundColor: '#ff7c43' }} />}
                    </div>
                  }
                  style={{
                    borderRadius: 12,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }}
                  bodyStyle={{ padding: '20px' }}
                >
                  <Tabs 
                    activeKey={activeTab} 
                    onChange={setActiveTab}
                    items={tabItems}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Second Row of Stats Cards (Admin specific) */}
          <Row gutter={[24, 24]} style={{ marginTop: shouldShowTasks ? 24 : 0 }}>
            <Col xs={24} sm={12} lg={6} className={`${isSUPER_ADMIN == true ? "" : "d-none"}`}>
              <Link to={"/tenants"}>
                <Card
                  style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                  hoverable
                >
                  <Statistic
                    title={<span style={{ fontSize: 14, color: '#666' }}>Total Tenants</span>}
                    value={Tenants.length}
                    prefix={<BankOutlined style={{ fontSize: 28, color: '#9254de' }} />}
                    valueStyle={{ fontSize: 32, fontWeight: 'bold', color: '#9254de' }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Tag color="purple">Active Subscriptions</Tag>
                  </div>
                </Card>
              </Link>
            </Col>

            <Col xs={24} sm={12} lg={6} className={`${isSUPER_ADMIN == true ? "" : "d-none"}`}>
              <Link to={"/complaints"}>
                <Card
                  style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                  hoverable
                >
                  <Statistic
                    title={<span style={{ fontSize: 14, color: '#666' }}>Total Complaints</span>}
                    value={Complaints.length}
                    prefix={<WarningOutlined style={{ fontSize: 28, color: '#ff4d4f' }} />}
                    valueStyle={{ fontSize: 32, fontWeight: 'bold', color: '#ff4d4f' }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">Need attention</Text>
                  </div>
                </Card>
              </Link>
            </Col>

            {(isSUPER_ADMIN || isTenantAdmin || isADMIN) && (
              <Col xs={24} sm={12} lg={6}>
                <Link to={"/users"}>
                  <Card
                    style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                    hoverable
                  >
                    <Statistic
                      title={<span style={{ fontSize: 14, color: '#666' }}>Total Users</span>}
                      value={TenantsUsers.length}
                      prefix={<TeamOutlined style={{ fontSize: 28, color: '#13c2c2' }} />}
                      valueStyle={{ fontSize: 32, fontWeight: 'bold', color: '#13c2c2' }}
                    />
                    <div style={{ marginTop: 12 }}>
                      <Text type="secondary">Active team members</Text>
                    </div>
                  </Card>
                </Link>
              </Col>
            )}

            <Col xs={24} sm={12} lg={6} className={`${isTenantAdmin ? "" : "d-none"}`}>
              <Link to={"/sub-tenants"}>
                <Card
                  style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                  hoverable
                >
                  <Statistic
                    title={<span style={{ fontSize: 14, color: '#666' }}>Sub Tenants</span>}
                    value={(decodedToken?.subTenantIds === "" || decodedToken?.subTenantIds === undefined) ? 0 : decodedToken?.subTenantIds.split(",").length}
                    prefix={<AppstoreOutlined style={{ fontSize: 28, color: '#fa8c16' }} />}
                    valueStyle={{ fontSize: 32, fontWeight: 'bold', color: '#fa8c16' }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Text type="secondary">Associated locations</Text>
                  </div>
                </Card>
              </Link>
            </Col>
          </Row>

          {/* Storage Instructions Section */}
          {StorageInst && StorageInst.length > 0 && (
            <Row gutter={24} style={{ marginTop: 24, marginBottom: 24 }}>
              <Col span={24}>
                <Card
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FormOutlined style={{ fontSize: 18, color: '#52c41a' }} />
                      <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        Storage Instructions
                      </span>
                    </div>
                  }
                  style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
                >
                  <Row gutter={[16, 16]}>
                    {StorageInst.map((item, index) => (
                      <Col xs={24} md={12} key={index}>
                        <Card
                          size="small"
                          style={{
                            background: '#fafafa',
                            borderRadius: 8,
                            borderLeft: `4px solid ${index % 2 === 0 ? '#1890ff' : '#52c41a'}`
                          }}
                        >
                          <div>
                            <Text strong>Instruction:</Text>
                            <Text style={{ marginLeft: 8 }}>{item.instruction}</Text>
                          </div>
                          <div style={{ marginTop: 8 }}>
                            <Tag color="blue">Storage: {item.storageDTO?.name}</Tag>
                            <Tag color="geekblue">Department: {item.institutionDTO?.institutionName}</Tag>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </Col>
            </Row>
          )}
        </Content>
        <FooterDashboard />
      </Layout>

      {/* Add Note Modal Component (for MANAGER) */}
      <AddNoteModal
        visible={noteModalVisible}
        task={selectedTask}
        onClose={() => {
          setNoteModalVisible(false);
          setSelectedTask(null);
        }}
        onNoteAdded={handleNoteAdded}
        currentUser={decodedToken}
        apiService={CleaningTraceabilityApi}
      />

      {/* View Note Modal Component (for SYSTEM_USER) */}
      <AntModal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <span>Manager's Note</span>
          </div>
        }
        open={viewNoteModalVisible}
        onCancel={() => {
          setViewNoteModalVisible(false);
          setViewingTask(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setViewNoteModalVisible(false);
            setViewingTask(null);
          }}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {viewingTask && (
          <div>
            {/* Task Details Section */}
            <div style={{ marginBottom: 20, padding: '16px', background: '#f5f5f5', borderRadius: 8 }}>
              <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 12 }}>
                Task Details:
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <Tag color="blue" style={{ fontSize: '13px', padding: '4px 12px' }}>
                  {viewingTask.taskName}
                </Tag>
                <Tag color="geekblue" style={{ fontSize: '13px', padding: '4px 12px' }}>
                  {viewingTask.department || "N/A"}
                </Tag>
                <Tag color="cyan" style={{ fontSize: '13px', padding: '4px 12px' }}>
                  {viewingTask.area || "N/A"}
                </Tag>
                <Tag 
                  color={viewingTask.status === "completed" ? "green" : "orange"} 
                  style={{ fontSize: '13px', padding: '4px 12px' }}
                >
                  {viewingTask.status?.toUpperCase() || "PENDING"}
                </Tag>
                {viewingTask.frequency && (
                  <Tag 
                    color={viewingTask.frequency === "DAILY" ? "blue" : viewingTask.frequency === "WEEKLY" ? "green" : "orange"}
                    style={{ fontSize: '13px', padding: '4px 12px' }}
                  >
                    {viewingTask.frequency}
                  </Tag>
                )}
              </div>
              {viewingTask.scheduledDate && (
                <div style={{ marginTop: 12 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    <CalendarOutlined /> Scheduled: {moment(viewingTask.scheduledDate).format('DD/MM/YYYY HH:mm')}
                  </Text>
                </div>
              )}
            </div>

            {/* Note Content Section */}
            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 12 }}>
                Note Content:
              </Text>
              <div style={{ 
                padding: '16px', 
                background: '#fff7e6', 
                borderRadius: 8,
                border: '1px solid #ffd591'
              }}>
                <Text style={{ fontSize: 14, lineHeight: 1.6 }}>
                  {viewingTask.managerNote || "No note has been added for this task yet."}
                </Text>
              </div>
            </div>

            {/* Info Message */}
            <div style={{ 
              marginTop: 16, 
              padding: '8px 12px', 
              background: '#e6f7ff', 
              borderRadius: 6,
              border: '1px solid #91d5ff'
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                💡 This note was added by the manager for reference and quality tracking purposes.
              </Text>
            </div>
          </div>
        )}
      </AntModal>
    </Layout>
  );
};

export default AdminPanel;